import { CtaType } from "@/components/molecules/CtaButtonGroup";
import SectionHeader from "./shared/SectionHeader";

/**
 * DarkTwoColCardsSection — full-bleed dark section with a centered
 * eyebrow + 2-line title + subtitle + CTA pair, followed by two
 * side-by-side dark cards. Each card renders a title with an accent
 * word, a description, a row of tag pills, and an optional mockup
 * image. Used by feature V2 pages for the "SCALE" block.
 */

export type DarkTwoColCard = {
  title: string;
  titleAccent?: string;
  accentColor?: string;
  description: string;
  tags: string[];
  image?: string;
};

export type DarkTwoColCardsSectionData = {
  eyebrow?: string;
  titleMuted?: string;
  titleBright?: string;
  title?: string;
  subtitle?: string;
  ctas?: CtaType[];
  cards: DarkTwoColCard[];
};

const Card = ({ card }: { card: DarkTwoColCard }) => {
  const accentClass = card.accentColor ?? "text-[#D4F75E]";
  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm md:p-10">
      <h3 className="font-inter text-[22px] font-semibold text-white md:text-[26px]">
        {card.title}
        {card.titleAccent ? (
          <>
            {" "}
            <span className={accentClass}>{card.titleAccent}</span>
          </>
        ) : null}
      </h3>
      <p className="mt-4 font-inter text-[14px] leading-relaxed text-white/70 md:text-[15px]">
        {card.description}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {card.tags.map((t) => (
          <span
            key={t}
            className="rounded-full bg-white px-3 py-1.5 font-inter text-[13px] font-medium text-neutralBlack"
          >
            {t}
          </span>
        ))}
      </div>
      {card.image ? (
        <div className="mt-auto pt-2">
          <div className="relative aspect-[5/4] w-full">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={card.image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </div>
        </div>
      ) : (
        <div className="mt-auto aspect-[5/4] w-full rounded-2xl bg-gradient-to-br from-white/10 to-white/[0.02]" />
      )}
    </div>
  );
};

const DarkTwoColCardsSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  title,
  subtitle,
  ctas,
  cards,
}: DarkTwoColCardsSectionData) => (
  <section className="relative w-full overflow-hidden bg-neutralBlack">
    <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
      <SectionHeader
        eyebrow={eyebrow}
        titleMuted={titleMuted}
        titleBright={titleBright}
        title={title}
        subtitle={subtitle}
        ctas={ctas}
        variant="dark-lime"
        align="center"
      />
      <div className="mx-auto mt-12 grid max-w-6xl gap-5 md:mt-16 md:grid-cols-2 md:gap-6">
        {cards.map((c) => (
          <Card key={c.title} card={c} />
        ))}
      </div>
    </div>
  </section>
);

export default DarkTwoColCardsSection;
